export interface Analytics {
    /**
     * initialize the analytics call chain.
     */
    initialize(): Promise<void>;

    /**
     * Record the event
     */
    event(type: string, params?: Record<string, any>): Promise<void>;

    /**
     * Called when the analytics system requires a full reload.
     */
    reload(): Promise<void>;

    /**
     * This may reload it.
     */
    close(): Promise<void>;
}


export interface AnalyticsDescription {
    name: string;
    description: string;

    actualFeatures: number[];
    actualPurposes: number[];
    actualSpecialFeatures: number[];
    actualSpecialPurposes: number[];

    policyUrl: string;
    deviceDisclosureUrl: string|null;
}


export interface AnalyticsVendor {
    id: string;

    strictlyRequired: boolean;
    inInitialActivations: boolean;

    build: () => Analytics;
    describe: () => Promise<AnalyticsDescription>|AnalyticsDescription;
}


class AnalyticsManager {
    private static MANAGER_KEY = "ANALYTICS_MANAGEMENT_V4_LIST";
    private static CONFIGURED_KEY = "ANALYTICS_MANAGEMENT_V4_CONFIGURED";
    private vendorList: Map<string, AnalyticsVendor> = new Map();
    private active: Map<string, Analytics> = new Map();
    private _internal: Analytics[] = [];

    push(vendor: AnalyticsVendor) {
        this.vendorList.set(vendor.id, vendor);
    }

    pushInternal(vendor: Analytics) {
        this._internal.push(vendor);
    }

    get vendors(): AnalyticsVendor[] {
        return Array.from(this.vendorList.values());
    }

    get activeVendors(): AnalyticsVendor[] {
        return Array.from(this.active.keys()).map(k => this.vendorList.get(k)!);
    }

    private __vendorKey() {
        const list = this.vendors.map(s => s.id);
        list.sort();
        return list.join(";");
    }

    isConfigured() {
        return window.localStorage.getItem(AnalyticsManager.CONFIGURED_KEY) === this.__vendorKey();
    }

    setConfigured() {
        window.localStorage.setItem(AnalyticsManager.CONFIGURED_KEY, this.__vendorKey());
    }
    
    async reload(): Promise<void> {
        const changes = [];
        for (let vendor of this.active.values()) {
            changes.push(vendor.reload());
        }
        await Promise.all(changes);
    }

    async update(active: string[], skipStore=false): Promise<void> {
        const keep = Array.from(this.vendorList.values()).filter(({strictlyRequired}) => strictlyRequired).map(s => s.id);

        const target_active = new Set([...keep, ...active]);
        const previous_active = new Set(this.active.keys());
        const removed = Array.from(this.active.keys()).filter(x => !target_active.has(x));
        const added = [...target_active].filter(x => !previous_active.has(x));

        if (!skipStore) {
            window.localStorage.setItem(AnalyticsManager.MANAGER_KEY, JSON.stringify(Array.from(target_active)));
        }

        const waiting = [];
        for (let vendor of removed) {
            console.debug(`[Analytics] Removing ${vendor}`);
            waiting.push(this.active.get(vendor)!.close());
            this.active.delete(vendor);
        }
        for (let vendor of added) {
            const description = this.vendorList.get(vendor);
            if (!description) continue;
            const analytics = description.build();
            this.active.set(vendor, analytics);
            console.debug(`[Analytics] Activating ${vendor}`);
            waiting.push(analytics.initialize());
        }

        await Promise.all(waiting);
    }

    async event(type: string, params: Record<string, any>) {
        const waiting = []
        for (let value of this.active.values()) {
            waiting.push(value.event(type, params));
        }
        for (let internal of this._internal) {
            waiting.push(internal.event(type, params));
        }
        await Promise.all(waiting);
    }

    async start() {
        const rawList = window.localStorage.getItem(AnalyticsManager.MANAGER_KEY);
        
        let list: string[];
        if (!rawList) {
            list = this.vendors.filter(v => v.strictlyRequired || v.inInitialActivations).map(v => v.id)
        } else {
            list = JSON.parse(rawList);
        }
        console.log("[Analytics] Activating the following vendors:", list);
        console.debug("[Analytics] All available vendors:", this.vendors.map(v => v.id));

        try {
            await this.update(list, /* skipStore= */true);
        } catch (e) {
            window.reportError(e, {telemetry: false});
        } finally {
            if (process.env.NODE_ENV === 'development') {
                console.debug("[Analytics] Activated.");
            }
            window.addEventListener("storage", (event) => {
                if (event.key !== AnalyticsManager.MANAGER_KEY) return;
                let list: string[];
                if (!event.newValue) {
                    list = this.vendors.filter(v => v.strictlyRequired || v.inInitialActivations).map(v => v.id)
                } else {
                    list = JSON.parse(event.newValue);
                }

                console.debug("[Analytics] Analytics changed from the outside. New vendor list:", list);
                this.update(list, false).catch((e) => window.reportError(e, {telemetry: false}));
            });
        }
    }
}


export default new AnalyticsManager();

